import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Search, BookOpen, Filter, ChevronLeft, ChevronRight, ArrowUpDown, Globe, Building2, Calendar } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const OC = { care: "#10b981", utility: "#3b82f6", creative: "#a855f7", extraction: "#ef4444" };

export const ResearchExplorer = () => {
  const [papers, setPapers] = useState([]);
  const [total, setTotal] = useState(0);
  const [corpusTotal, setCorpusTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [orientation, setOrientation] = useState("");
  const [source, setSource] = useState("");
  const [sort, setSort] = useState("citations");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  // Debounce the search input so typing "heart" fires one request, not five.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 1 whenever the *committed* query changes.
  useEffect(() => { setPage(1); }, [debouncedSearch, orientation, source, sort]);

  const fetchPapers = useCallback(async () => {
    setLoading(true);
    try {
      const body = { search: debouncedSearch, orientation, source, sort, page, limit: 25 };
      const res = await axios.post(`${API}/research/papers`, body);
      setPapers(res.data.papers || []);
      setTotal(Math.max(0, res.data.total || 0));
      setCorpusTotal(prev => prev || res.data.corpus_total || (!debouncedSearch && !orientation && !source ? res.data.total : 0) || prev);
      setPages(res.data.pages || 1);
    } catch (e) {
      setPapers([]);
      setTotal(0);
      setPages(1);
    }
    setLoading(false);
  }, [debouncedSearch, orientation, source, sort, page]);

  useEffect(() => { fetchPapers(); }, [fetchPapers]);

  const handleSearch = (e) => {
    e.preventDefault();
    // Commit immediately on submit (skip the debounce)
    setDebouncedSearch(search.trim());
    setPage(1);
  };

  return (
    <div className="space-y-4" data-testid="research-explorer">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <BookOpen className="w-5 h-5 text-cyan-400" />
        <div>
          <h2 className="text-lg font-bold text-white">Paper Explorer</h2>
          <p className="text-[10px] text-slate-500">Search and filter {(corpusTotal || total).toLocaleString()} research papers</p>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
        <form onSubmit={handleSearch} className="flex gap-3 mb-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder='Search by keyword — e.g. "heart", "climate", "fairness", "bias"…'
              className="w-full pl-10 pr-4 py-2.5 bg-black/30 border border-white/[0.08] rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/30"
              data-testid="paper-search-input" />
          </div>
          <button type="submit" className="px-5 py-2.5 bg-cyan-600/30 hover:bg-cyan-600/50 border border-cyan-500/20 rounded-lg text-xs text-cyan-300 font-bold transition-all"
            data-testid="paper-search-btn">Search</button>
        </form>
        <div className="flex items-center gap-3 flex-wrap">
          <Filter className="w-3.5 h-3.5 text-slate-500" />
          <select value={orientation} onChange={e => { setOrientation(e.target.value); setPage(1); }}
            className="px-3 py-1.5 bg-black/30 border border-white/[0.08] rounded-lg text-[10px] text-white"
            data-testid="paper-filter-orientation">
            <option value="">All Orientations</option>
            <option value="care">Care</option>
            <option value="utility">Utility</option>
            <option value="creative">Creative</option>
            <option value="extraction">Extraction</option>
          </select>
          <select value={source} onChange={e => { setSource(e.target.value); setPage(1); }}
            className="px-3 py-1.5 bg-black/30 border border-white/[0.08] rounded-lg text-[10px] text-white"
            data-testid="paper-filter-source">
            <option value="">All Sources</option>
            <option value="semantic_scholar">Semantic Scholar</option>
            <option value="arxiv">arXiv</option>
            <option value="huggingface">HuggingFace</option>
            <option value="ssrn">SSRN</option>
            <option value="stanford_hai">Stanford HAI</option>
          </select>
          <select value={sort} onChange={e => { setSort(e.target.value); setPage(1); }}
            className="px-3 py-1.5 bg-black/30 border border-white/[0.08] rounded-lg text-[10px] text-white"
            data-testid="paper-sort">
            <option value="citations">{debouncedSearch ? "Most Relevant" : "Most Cited"}</option>
            <option value="year">Most Recent</option>
            <option value="title">Alphabetical</option>
          </select>
          <span className="text-[10px] text-slate-500 ml-auto">{total.toLocaleString()} results</span>
        </div>
      </div>

      {/* Papers Table */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="w-6 h-6 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-xs text-slate-500">Loading papers...</p>
          </div>
        ) : papers.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-sm text-slate-400">No papers found</p>
            <p className="text-[10px] text-slate-500 mt-1">Try a different search query or filter</p>
          </div>
        ) : (
          <div className="divide-y divide-white/[0.04]">
            {/* Column Header */}
            <div className="grid grid-cols-[1fr_100px_70px_70px_100px] gap-2 px-4 py-2.5 bg-white/[0.01]">
              <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">Paper</span>
              <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider text-center">
                <ArrowUpDown className="w-3 h-3 inline mr-1" />Orientation</span>
              <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider text-center">Year</span>
              <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider text-center">Cites</span>
              <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider text-center">Source</span>
            </div>

            {papers.map((p, i) => (
              <div key={p.paper_title || `paper-${i}`} className="hover:bg-white/[0.02] transition-colors cursor-pointer"
                onClick={() => setExpanded(expanded === i ? null : i)} data-testid={`paper-row-${i}`}>
                <div className="grid grid-cols-[1fr_100px_70px_70px_100px] gap-2 px-4 py-3 items-center">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-white truncate">{(p.paper_title || "").replace(/<[^>]+>/g, "")}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {p.institution && p.institution !== "Unknown" && (
                        <span className="text-[9px] text-slate-500 flex items-center gap-1">
                          <Building2 className="w-2.5 h-2.5" />{p.institution}
                        </span>
                      )}
                      {p.country && p.country !== "Unknown" && (
                        <span className="text-[9px] text-slate-500 flex items-center gap-1">
                          <Globe className="w-2.5 h-2.5" />{p.country}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-center">
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold"
                      style={{ background: `${OC[p.value_orientation] || "#475569"}15`, color: OC[p.value_orientation] || "#475569" }}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: OC[p.value_orientation] || "#475569" }} />
                      {p.value_orientation}
                    </span>
                  </div>
                  <span className="text-[10px] text-slate-400 text-center flex items-center justify-center gap-1">
                    <Calendar className="w-3 h-3" />{p.year}
                  </span>
                  <span className="text-[10px] font-bold text-cyan-400 text-center">{(p.citations || 0).toLocaleString()}</span>
                  <span className="text-[9px] text-slate-500 text-center">{p.source === "semantic_scholar" ? "S2" : p.source === "huggingface" ? "HF" : p.source === "ssrn" ? "SSRN" : p.source === "stanford_hai" ? "HAI" : "arXiv"}</span>
                </div>
                {/* Expanded abstract */}
                {expanded === i && p.abstract && (
                  <div className="px-4 pb-3 animate-fade-in">
                    <p className="text-[10px] text-slate-400 leading-relaxed bg-white/[0.02] rounded-lg p-3 border border-white/[0.04]">
                      {p.abstract}
                    </p>
                    {p.fields?.length > 0 && (
                      <div className="flex gap-1.5 mt-2 flex-wrap">
                        {p.fields.map((f, fi) => (
                          <span key={fi} className="px-2 py-0.5 bg-indigo-500/10 text-indigo-300 text-[8px] rounded-full">{f}</span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}
            className="p-2 rounded-lg bg-white/[0.03] border border-white/[0.06] text-slate-400 hover:bg-white/[0.06] disabled:opacity-30 transition-all"
            data-testid="paper-prev-page">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-[10px] text-slate-400">Page {page} of {pages}</span>
          <button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page >= pages}
            className="p-2 rounded-lg bg-white/[0.03] border border-white/[0.06] text-slate-400 hover:bg-white/[0.06] disabled:opacity-30 transition-all"
            data-testid="paper-next-page">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};
